import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Camera, Compass, Satellite, Sparkles, CheckCircle2, Filter } from "lucide-react";
import { toast } from "sonner";
import DashboardShell from "../components/shell/DashboardShell";
import PropertyImage from "../components/PropertyImage";
import { RetakePhotoDialog } from "../components/RetakePhotoDialog";
import { TYPE_BY_VALUE } from "../lib/propertyTypes";
import api, { formatApiError } from "../lib/api";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "auto", label: "Auto-framed" },
  { id: "manual", label: "Manually set" },
  { id: "aerial", label: "Aerial" },
];

function propertyStatus(p) {
  if (p.image_source === "staticmap") return "aerial";
  if (p.image_heading !== null && p.image_heading !== undefined) return "manual";
  return "auto";
}

/**
 * Retake Photo — Batch Mode
 *
 * Lets an investor re-aim every property's hero image in one sitting.
 * Grid of cards (thumbnail + name + auto/manual/aerial badge + Adjust button)
 * that each open the existing RetakePhotoDialog. After save, the card's
 * refreshKey bumps so the new framing renders immediately. Filter bar lets
 * investors focus on one subset (e.g. "auto-framed only" before a quarterly
 * investor-report refresh).
 */
export default function RetakePhotoBatch() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null); // property currently in the dialog
  const [versions, setVersions] = useState({}); // pid → version (bump to refresh thumbnail)

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/properties");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { all: items.length, auto: 0, manual: 0, aerial: 0 };
    for (const p of items) c[propertyStatus(p)]++;
    return c;
  }, [items]);

  const visible = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((p) => propertyStatus(p) === filter);
  }, [items, filter]);

  const onSaved = async (patch) => {
    if (!active) return;
    // Update local state + bump refreshKey so thumbnail re-fetches
    setItems((prev) => prev.map((p) => (p.id === active.id ? { ...p, ...patch } : p)));
    setVersions((v) => ({ ...v, [active.id]: (v[active.id] || 0) + 1 }));
  };

  return (
    <DashboardShell>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/dashboard/properties" className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="retake-batch-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Properties
          </Link>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="overline text-slate-500 flex items-center gap-1"><Camera className="w-3 h-3" /> Batch mode</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>Retake photos</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Re-aim every property's hero image before your next investor-report refresh. Click any card to adjust the Street View bearing or switch to aerial satellite view.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-sm border border-slate-200 bg-white p-1" data-testid="retake-batch-filter">
            <Filter className="w-3.5 h-3.5 text-slate-400 ml-1" />
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1 rounded-sm text-xs font-mono uppercase tracking-widest transition ${filter === f.id ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
                data-testid={`retake-batch-filter-${f.id}`}
              >
                {f.label} <span className="ml-1 opacity-70">{counts[f.id] ?? 0}</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="retake-batch-skeleton">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-slate-100 border border-slate-200 rounded-sm h-64 animate-pulse" />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-sm p-12 text-center" data-testid="retake-batch-empty">
            <Sparkles className="w-6 h-6 text-slate-400 mx-auto mb-2" />
            <div className="text-sm text-slate-600">
              {items.length === 0
                ? "No properties yet — add one to start framing."
                : `Nothing matches the "${FILTERS.find((f) => f.id === filter)?.label}" filter.`}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="retake-batch-grid">
            {visible.map((p) => (
              <BatchCard
                key={p.id}
                property={p}
                version={versions[p.id] || 0}
                onAdjust={() => setActive(p)}
              />
            ))}
          </div>
        )}
      </div>

      <RetakePhotoDialog
        property={active}
        open={!!active}
        onClose={() => setActive(null)}
        onSaved={onSaved}
      />
    </DashboardShell>
  );
}

function BatchCard({ property, version, onAdjust }) {
  const p = property;
  const status = propertyStatus(p);
  const typeMeta = TYPE_BY_VALUE[p.property_type];
  const badge =
    status === "aerial"
      ? { label: "Aerial", icon: <Satellite className="w-3 h-3" />, tone: "bg-violet-50 text-violet-700 border-violet-200" }
      : status === "manual"
        ? { label: `Manual · ${Math.round(p.image_heading)}°`, icon: <Compass className="w-3 h-3" />, tone: "bg-amber-50 text-amber-700 border-amber-200" }
        : { label: "Auto", icon: <CheckCircle2 className="w-3 h-3" />, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" };

  return (
    <button
      type="button"
      onClick={onAdjust}
      className="group text-left bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-slate-900 hover:shadow-md transition-all"
      data-testid={`retake-batch-card-${p.id}`}
    >
      <div className="relative w-full h-40 bg-slate-100 overflow-hidden">
        <PropertyImage pid={p.id} size="480x240" className="w-full h-40" rounded="rounded-none" alt={p.name} refreshKey={version} />
        <div className={`absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${badge.tone}`}>
          {badge.icon} {badge.label}
        </div>
        <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-slate-900/85 text-white text-[10px] font-mono uppercase tracking-widest opacity-0 group-hover:opacity-100 transition">
          <Camera className="w-3 h-3" /> Adjust
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-center gap-1.5">
          <span className="text-sm">{typeMeta?.emoji || "📦"}</span>
          <div className="font-medium text-sm text-slate-900 truncate">{p.name}</div>
        </div>
        <div className="text-xs text-slate-500 truncate mt-0.5">{p.formatted_address || p.address || "—"}</div>
      </div>
    </button>
  );
}
